<template>
  <proa-card flat class="invite-new-user-card">
    <div class="content-wrapper">
      <router-link :to="{ name: 'root' }" class="logo">
        <img :src="require('@/assets/images/logo.png')"/>
      </router-link>
      <template v-if="rejected">
        <div class="result">
          <img :src="require('@/assets/images/reject.png')"/>
        </div>
        <div class="result-rejected">{{ $t('pages.inviteUser.inviteCard.rejectedInvitationHeader') }}</div>
        <div class="result-body">{{ $t('pages.inviteUser.inviteCard.rejectedInvitationBody') }}</div>
        <div>
          <proa-button
            class="w-100"
            type="submit"
            variant="outline-primary"
            @click.native="onClickToTop"
          >
            {{ $t('pages.inviteUser.inviteCard.toTopOfSharewind') }}
          </proa-button>
        </div>
      </template>
      <template v-else-if="registered">
        <div class="result">
          <img :src="require('@/assets/images/approve.png')"/>
        </div>
        <div>{{ $t('pages.inviteUser.inviteCard.registeredAccount') }}</div>
        <div class="font-weight-w6 my-3">{{ organizationName }}</div>
        <div style="margin-bottom: 40px;">{{ $t('pages.inviteUser.inviteCard.wasRegisteredMember') }}</div>
        <div>
          <proa-button
            class="w-100"
            type="submit"
            variant="outline-primary"
            @click.native="onClickToRootLocation"
          >
            {{ $t('pages.inviteUser.inviteCard.toTopOfSharewind') }}
          </proa-button>
        </div>
      </template>
      <template v-else-if="newUser != null">
        <span class="create-new-account-header">{{ $t('common.createNewAccount') }}</span>
        <validation-observer v-slot="{ handleSubmit }">
          <form novalidate @submit.prevent="() => { validated = true; handleSubmit(onSubmitNewUser) }" class="create-new-account-form">
            <div class="name-fields">
              <validation-provider
                :name="$t('models.user.attributes.lastName')"
                rules="required"
                v-slot="{ errors }"
              >
                <proa-input-group :invalidFeedback="validated ? errors[0] : null">
                  <template slot="input">
                    <proa-input
                      v-model="newUser.lastName"
                      type="text"
                      :placeholder="$t('models.user.attributes.lastName')"
                      dark
                    />
                  </template>
                </proa-input-group>
              </validation-provider>
              <validation-provider
                :name="$t('models.user.attributes.firstName')"
                rules="required"
                v-slot="{ errors }"
              >
                <proa-input-group :invalidFeedback="validated ? errors[0] : null">
                  <template slot="input">
                    <proa-input
                      v-model="newUser.firstName"
                      type="text"
                      :placeholder="$t('models.user.attributes.firstName')"
                      dark
                    />
                  </template>
                </proa-input-group>
              </validation-provider>
            </div>
            <validation-provider
              :name="$t('models.user.attributes.password')"
              rules="required|min:8|password"
              v-slot="{ errors }"
              vid="password"
            >
              <proa-input-group :invalidFeedback="validated ? errors[0] : null">
                <template slot="input">
                  <proa-input
                    v-model="newUser.password"
                    type="password"
                    :placeholder="$t('models.user.attributes.password')"
                    dark
                  />
                </template>
              </proa-input-group>
            </validation-provider>
            <validation-provider
              :name="$t('models.user.attributes.passwordConfirmation')"
              rules="required|confirmed:password"
              v-slot="{ errors }"
            >
              <proa-input-group :invalidFeedback="validated ? errors[0] : null" class="mb-0">
                <template slot="input">
                  <proa-input
                    v-model="newUser.passwordConfirmation"
                    type="password"
                    :placeholder="$t('models.user.attributes.passwordConfirmation')"
                    dark
                  />
                </template>
              </proa-input-group>
            </validation-provider>
            <span class="password-rules">{{ $t('models.user.passwordRules') }}</span>
            <div class="mt-4">
              <confirm-terms-check-box
                :agreedTerms="agreedTerms"
                @onChange="agreedTerms = !agreedTerms"
              />
            </div>
            <proa-button
              type="submit"
              class="w-100"
              variant="primary"
              :disabled="!agreedTerms"
              :loading="httpRequesting"
            >
              {{ $t('common.createNewAccount') }}
            </proa-button>
          </form>
        </validation-observer>
      </template>
      <template v-else>
        <span class="welcome-message">{{ $t('common.welcomeToSharewind') }}</span>
        <div class="invite-from">
          <span class="font-weight-w6">{{ organizationName }}</span>
          <span>{{ $t('pages.inviteUser.inviteCard.receivedInvitation') }}</span>
        </div>
        <div class="create-new-account-message">{{ $t('pages.inviteUser.inviteCard.createNewAccountMessage') }}</div>
        <div>
          <proa-button
            class="w-100"
            variant="primary"
            @click.native="buildNewUser"
          >
            {{ $t('common.createNewAccount') }}
          </proa-button>
        </div>
        <div
          :class="['reject-invitation', { 'disabled': httpRequesting }]"
          @click="$emit('onClickRejectInvitation')"
        >
          {{ $t('pages.inviteUser.inviteCard.rejectInvitation') }}
        </div>
      </template>
    </div>
  </proa-card>
</template>

<script>
import User from '@/store/models/User'

import ConfirmTermsCheckBox from '@/components/molecules/ConfirmTermsCheckBox'

export default {
  name: 'InviteSignedInUserCard',
  props: {
    organizationName: String,
    httpRequesting: Boolean,
    registered: Boolean,
    rejected: Boolean
  },
  components: {
    ConfirmTermsCheckBox
  },
  data() {
    return {
      newUser: null,
      validated: false,
      agreedTerms: false
    }
  },
  methods: {
    buildNewUser: function() {
      this.newUser = Object.assign(User.skeleton(), {
        password: '',
        passwordConfirmation: ''
      })
    },
    onClickToTop: function() {
      this.$router.push({ name: 'mySpace' })
    },
    onClickToRootLocation: function() {
      window.location = '/'
    },
    onSubmitNewUser: function() {
      this.$emit('onSubmitNewUser', this.newUser)
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/mediaquery";
@import "@/styles/variables";

.invite-new-user-card {
  width: 100%;

  @include mq(sm) {
    width: 400px;
  }
}

.content-wrapper {
  padding: 40px 50px;
  text-align: center;

  .logo {
    display: block;
    margin-bottom: 40px;

    img {
      width: 150px;
    }
  }

  .create-new-account-header {
    display: block;
    margin-top: -20px;
    font-weight: 600;
  }

  .create-new-account-form {
    margin-top: 40px;

    .name-fields {
      display: flex;

      span:first-child {
        display: block;
        margin-right: 10px;
      }
    }

    .password-rules {
      display: block;
      margin-top: 20px;
      white-space: pre-wrap;
      font-size: 12px;
      color: $light-text-color;
    }

    button[type="submit"] {
      margin-top: 40px;
    }
  }

  .result {
    margin-bottom: 20px;

    img {
      width: 60px;
    }
  }

  .result-rejected {
    color: $error-color;
  }

  .result-body {
    white-space: pre-wrap;
    margin: 40px 0;
  }

  .welcome-message {
    color: $primary-color;
    font-weight: 600;
  }

  .invite-from {
    margin: 40px 0;

    span {
      display: block;
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .create-new-account-message {
    white-space: pre-wrap;
    margin-bottom: 40px;
  }

  .reject-invitation {
    cursor: pointer;
    color: $link-color;
    margin-top: 40px;

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>
